/**
 * What the streaks sheet SAYS, as data — Blockari's `shell/streak-copy.ts` (c258375). The DOM half (`streak-sheet.ts`)
 * only draws these, so the wording is tested in node.
 *
 * Blockari's "Combos" is 21 Wild's CARD STREAK: the in-game streak multiplier (the 2X…6X callouts, the profile's Best
 * streak tile). "Win streaks" is also a tab here, so the in-game one is named for what builds it, cards: the tab and the
 * leaderboard board read "Card streaks", a row "5X streak". The on-screen word for the in-game thing is already
 * "streak" (Best streak, BEST STREAK on a share card), so nothing else is renamed.
 */

import type { TopStreaks } from '@/net/api';

export type StreakTab = 'cards' | 'wins';

/** The two tabs, in order. */
export const STREAK_TABS: readonly { value: StreakTab; label: string }[] = [
  { value: 'cards', label: 'Card streaks' },
  { value: 'wins', label: 'Win streaks' },
];

/** A card streak, named: "5X streak". */
export const cardTitle = (length: number): string => `${length}X streak`;
/** A win streak, named: "5 wins in a row". */
export const winTitle = (length: number): string => `${length} wins in a row`;
/** How many times: "1 time", "12 times". */
export const timesLabel = (n: number): string => (n === 1 ? 'time' : 'times');

/** The date a streak last happened, as the "Last:" line shows it: "Sep 27, 2026". */
export function lastDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The line under a row: "Last: Sep 27, 2026"; an ongoing win streak says when it "Started". */
export function whenLine(row: { last: string; ongoing?: boolean; started?: string }): string {
  return row.ongoing && row.started ? `Started ${lastDate(row.started)}` : `Last: ${lastDate(row.last)}`;
}

/** "Current: 2 wins in a row", or null when there is no current streak. */
export function currentLine(n: number): string | null {
  return n > 0 ? `Current: ${n} ${n === 1 ? 'win' : 'wins'} in a row` : null;
}

/** Which tab the sheet opens on: card streaks (what the Best streak tile shows) unless there are none. */
export function firstTab(s: Pick<TopStreaks, 'combos' | 'winRuns'>): StreakTab {
  return s.combos.length > 0 || s.winRuns.length === 0 ? 'cards' : 'wins';
}

/** The empty state of a tab: its title and the line under it. */
export function emptyCopy(tab: StreakTab): { title: string; text: string } {
  return tab === 'cards'
    ? { title: 'No card streaks yet', text: 'Play cards that score in a row and your best land here.' }
    : { title: 'No win streaks yet', text: 'Win two challenges in a row and the streak lands here. Open and expired challenges never count.' };
}

/**
 * Whether a profile has anything for the Streaks sheet to open on (the tile is only a button then): a card streak (a
 * stored best streak of 1 is the 2X callout) or two challenge wins.
 */
export function hasStreaks(p: { bestStreak?: number | undefined; challengesWon: number }): boolean {
  return (p.bestStreak ?? 0) >= 1 || p.challengesWon >= 2;
}
